/**
 * CorvusGlaive.js
 * The Corvus Glaive Enemy class
 */

import { Game } from "pixi_core";
import { EnemyAbilities, GameEvents } from "../Constants";
import { Enemy } from "./Enemy";
import { EnemyParticle } from "../game/particles/EnemyParticle";

export class CorvusGlaive extends Enemy {
    /**
     * @public
     * @param {String} enemyName
     * @param {Object} enemyData
     * @param {Number} index
     * @param {Boolean} [summoned] - Summoned by an ability
     */
    constructor(enemyName, enemyData, index, summoned) {
        super(enemyName, enemyData, index, summoned);

        const probStun = Game.utils.assertNumber(enemyData.ProbStun, 1);
        const probCorvusGlaive = Game.utils.assertNumber(enemyData.ProbCorvusGlaive, 1);
        const probRegeneration = Game.utils.assertNumber(enemyData.ProbRegeneration, 1);

        /** @private @type {Boolean} */
        this._isCharging = false;

        /** @private @type {Number} - Amount of HP regenerated by the Regeneration ability */
        this._regenHP = (Game.utils.assertNumber(enemyData.RegenHealthPercent, 1) * this._maxHp) / 100;

        // Single ability choice
        this._abilityChoices.addChoice(EnemyAbilities.eSTUN, probStun);
        this._abilityChoices.addChoice(EnemyAbilities.eCORVUS_GLAIVE, probCorvusGlaive);
        this._abilityChoices.addChoice(EnemyAbilities.eREGENERATION, probRegeneration);
    }

    /**
     * @protected
     * @override
     * @param {EnemyAbilities} abilityName
     * @param {{x: Number, y: Number}} startPos
     */
    _useAbility(abilityName, startPos) {
        switch (abilityName) {
            case EnemyAbilities.eSTUN:
                Game.fire(GameEvents.eGAME_ABILITY_JAM, this._stunNumber, this._stunTurns, startPos, this._onAbilityCompleted.bind(this));
                break;
            case EnemyAbilities.eCORVUS_GLAIVE:
                this._isCharging = true;
                Game.fire(GameEvents.eGAME_ABILITY_ENEMY_POWER_UP, this);
                this._chooseNextAction();
                break;
            case EnemyAbilities.eREGENERATION:
                Game.fire(GameEvents.eGAME_ABILITY_REGENERATION, this._regenHP, this._enemyName, this._onAbilityCompleted.bind(this));
                break;
            default:
                console.error("[CorvusGlaive._useAbility] Invalid ability name : " + abilityName);
                this._chooseNextAction();
        }
    }

    /**
     * The ability name is now hidden
     * @public
     * @override
     * @param {{x: Number, y: Number}} startPos
     */
    onAbilityNameCompleted(startPos) {
        if (this._preemptiveStrike) {
            this._chooseNextAction();
        } else {
            this._useAbility(this._selectedAbility, startPos);
        }
    }

    /**
     * Choose the next action in this enemy's attack phase
     * @protected
     * @override
     */
    _chooseNextAction() {
        if (this._nbActionsPerformed < this._nbActionsPerTurn && this._hp > 0) {
            ++this._nbActionsPerformed;

            if (this._currentAttackTimer > 0 || this._isCharging) {
                this._regularAttack();
            } else {
                let ability = this._chooseAbility();
                if (this._preemptiveStrike) {
                    this._preemptiveStrike = false;
                    ability = EnemyAbilities.eSTUN;
                } else {
                    while (this._hp > (this._maxHp * this._triggerHealthPercent) / 100 && ability === EnemyAbilities.eREGENERATION) {
                        ability = this._chooseAbility();
                    }
                }
                this._beginAbility(ability);
            }
        } else {
            this._onAttackPhaseCompleted();
        }
    }

    /**
     * The attack particle has reached its target
     * @protected
     * @override
     * @param {EnemyParticle} particle
     */
    _onDamageParticleCompleted(particle) {
        if (this._isCharging) {
            const chargedDamage = this._attackDamage * (this._chargingDamageBonus / 100);
            Game.fire(GameEvents.eHEROES_HIT, chargedDamage, particle);
            Game.fire(GameEvents.eGAME_ABILITY_ENEMY_POWER_DOWN, this);
            this._isCharging = false;
        } else {
            Game.fire(GameEvents.eHEROES_HIT, this._attackDamage, particle);
        }

        this._chooseNextAction();
    }
}
